// The conversation in the platform agent's panel. Wording from the Amigo design's copy sheet; `steps` labels the
// step list (keys in platformAgentConversation.js). The working line's labels are docs/platform-agent/contracts.md
// §5's table, in services/platform-agent/stepLabels.js.
export default {
  conversation: 'Conversation',
  working: 'Thinking…',
  stopped: 'Stopped. Nothing changed.',
  step_stopped: 'Stopped',
  steps_completed_one: '{{count}} step completed',
  steps_completed_other: '{{count}} steps completed',
  chart: {
    table: 'The figures in this chart',
    label: 'Label',
    value: 'Value',
  },
  // A form of several values Amigo needs at once (ask_form, contracts §5).
  form: {
    submit: 'Continue',
    skip: 'Skip for now',
    required: 'Fill this in to continue',
    generated: 'Created for you when you approve',
    step_of: 'Step {{index}} of {{total}}',
    on: 'On',
    off: 'Off',
    skipped: 'Skipped for now',
  },
  next_steps: 'Next steps',
  action_card: {
    select_restaurant: 'Select this restaurant',
    open_page: 'Opens in the dashboard',
  },
  not_sent: 'Not sent',
  failed: {
    text: 'Something went wrong on my side, so nothing changed. Try again in a moment.',
    retry: 'Try again',
  },
  question: {
    other: 'Something else…',
    send: 'Send',
    label: 'Question',
    type_answer: 'Something else? Type it below.',
    label_many: 'Questions',
    step_of: '{{index}} of {{total}}',
    previous: 'Previous question',
    next: 'Next question',
    dismiss: 'Not now',
  },
  unavailable: {
    title: {
      amigo: "Amigo isn't available right now.",
      assistant: "The assistant isn't available right now.",
    },
    text: 'Nothing has changed, and the rest of the dashboard works as normal. Try again in a few minutes.',
  },
  // A chat that goes on over several days (D4): a line before each day's first message, and Today to carry on.
  today: 'Today',
  when: {
    today: 'Today, {{time}}',
    yesterday: 'Yesterday, {{time}}',
    date: '{{date}}, {{time}}',
  },
  new_chat_line: "New chat for {{restaurant}}. {{previous}}'s chats are under Chats when you go back to it.",
  // A restaurant selected during a chat with no restaurant selected (D18): the chat carries on there, or a new one starts.
  carried_on_line: 'Now on {{restaurant}}. This chat carries on and is saved with its chats.',
  from_none_line:
    'New chat for {{restaurant}}. Your chat about {{earlier}} is under Chats, with no restaurant selected.',
  from_none_line_unnamed: 'New chat for {{restaurant}}. Your earlier chat is under Chats, with no restaurant selected.',
  // Someone else's chat, opened by our team (D4): whose it is, by who started it (contracts §6 `started_by.kind`).
  read_only: {
    whose: {
      foodamigos: "{{name}}'s chat (FoodAmigos)",
      owner: "{{name}}'s chat (owner)",
      staff: "{{name}}'s chat (staff)",
      partner: "{{name}}'s chat (partner)",
      other: "{{name}}'s chat",
      unnamed: "Someone else's chat",
    },
    text: "{{name}}'s chat. You can read it, but not reply or approve its cards.",
    text_unnamed: "Someone else's chat. You can read it, but not reply or approve its cards.",
    new_chat: 'New chat',
  },
  // A chat whose eve session can't go on (QA42): read only, and New chat takes the message that didn't go with it.
  ended: {
    text: 'This chat has ended. Start a new chat to go on.',
    new_chat: 'New chat',
    moved_line: 'New chat, because the last one had ended',
  },
  composer: {
    label: 'Message',
    placeholder: 'Ask a question, or describe a change',
    placeholder_answer: 'Type your answer',
    hint: 'Enter to send · Shift+Enter new line',
  },
  table: {
    show_all: 'Show all {{count}} rows',
    show_fewer: 'Show fewer',
  },
  steps: {
    areas: 'Checking what I can help with here',
    area_describe: 'Looking at {{area}}',
    restaurants: 'Finding the restaurant',
    read: 'Reading your data',
    propose: 'Preparing the change',
    change_set_get: 'Checking the change',
    change_set_undo: 'Preparing the undo',
    change_set_withdraw: 'Closing the card',
    gap_record: 'Noting this for our team',
    research: 'Looking up how this works',
    screen_find: 'Finding the right page',
    file_read: 'Reading your file',
    attach_chat_file: 'Preparing your file',
    shop_check: 'Checking your shop',
    area_describe_other: 'Looking at this area',
    other: 'Checking something',
  },
  // What a step that failed says it couldn't do (QL46), by the area it worked in; `other` is the label without an
  // area. contracts §5's Failed steps, kept the same as STEP_FAILED_LABELS in stepLabels.js (a test holds en and de).
  failed_steps: {
    restaurants: {
      other: "Couldn't find the restaurant",
    },
    read: {
      menu: "Couldn't read the menu",
      hours: "Couldn't read the opening hours",
      order_settings: "Couldn't read the order settings",
      my_info: "Couldn't read your details",
      marketing: "Couldn't read your marketing",
      gift_cards: "Couldn't read the gift cards",
      reports: "Couldn't read your numbers",
      other: "Couldn't read your data",
    },
    propose: {
      menu: "Couldn't prepare the menu change",
      hours: "Couldn't prepare the opening hours change",
      order_settings: "Couldn't prepare the order settings change",
      my_info: "Couldn't prepare the change to your details",
      marketing: "Couldn't prepare the marketing change",
      gift_cards: "Couldn't prepare the gift card change",
      other: "Couldn't prepare the change",
    },
    change_set_get: { other: "Couldn't check the change" },
    change_set_undo: { other: "Couldn't prepare the undo" },
    change_set_withdraw: { other: "Couldn't close the card" },
    research: { other: "Couldn't look up how this works" },
    screen_find: { other: "Couldn't find the page" },
    file_read: { other: "Couldn't read your file" },
    attach_chat_file: { other: "Couldn't prepare your file" },
    shop_check: { other: "Couldn't open your shop" },
  },
  // The areas of contracts §0 as the steps name them ("Looking at {{area}}").
  areas: {
    menu: 'the menu',
    hours: 'the opening hours',
    order_settings: 'the order settings',
    my_info: 'your restaurant info',
    marketing: 'marketing',
    reports: 'the reports',
  },
};
